'use client';

import { c as _c } from "react/compiler-runtime";
import type { DialogOptions } from '@morpho-org/ui-v6/client';
import type { Address } from 'viem';
import { type ChainId, MathLib } from '@morpho-org/blue-sdk';
import { InterfaceToken } from '@morpho-org/dapp-core';
import { InsufficientBlueBalanceForInKindRedeemError } from '@morpho-org/morpho-sdk';
import { getChainAddress } from '@morpho-org/morpho-sdk/addresses';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { getMarketName } from '@morpho-org/ui-v6/utils';
import { Button, Icon } from '@repo/ui';
import { Alert, Close, LoaderGradient } from '@repo/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useWalletClient } from 'wagmi';
import type { InKindRedemptionEntrySource } from '@/lib/analytics/client/events';
import { useVaultV2RecapData, type VaultV2RecapSource } from '@/components/Bundler/ConfirmDialog/Recaps/hooks/useVaultV2RecapData';
import VaultV2InKindRedemptionRecap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV2InKindRedemptionRecap';
import { ErrorServiceUnavailable } from '@/components/common/Error/ErrorServiceUnavailable';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { refetchLendPositionsUntilIndexed } from '@/hooks/positions/refetchLendPositionsUntilIndexed';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import { useReviewConfirmGate } from '@/hooks/useDeferredSubmit';
import useFocusOnMount from '@/hooks/useFocusOnMount';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
import { buildReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { InterfaceOperation } from '@/operations/operations.types';
import InfoCallout from '../InfoCallout';
import { type InKindRedemptionMarket, inKindRedemptionPenaltyRate, type InKindRedemptionPreview, realisedInKindRedemption, toSafeInKindPreview } from './inKindRedemption.helpers';
import InKindRedemptionPortfolioAlert from './InKindRedemptionPortfolioAlert';
import { useVaultV2InKindRedeemTransaction } from './useVaultV2InKindRedeemTransaction';

/**
 * How long a re-preparation may run before the CTA admits it is busy. Long enough that a poll
 * resolving normally never paints, short enough that a genuinely stuck build still says so.
 */
const PREPARATION_SPINNER_DELAY_MS = 400;
interface VaultV2InKindRedemptionReviewDialogProps {
  source: InKindRedemptionEntrySource;
  vaultAddress: Address;
  chainId: ChainId;
  preview: InKindRedemptionPreview;
  /** Force-deallocate penalty rate (WAD), frozen with the preview: a change invalidates it. */
  penalty: bigint;
  userPosition: bigint;
  market: InKindRedemptionMarket;
  dialogOptions?: DialogOptions;
  onTxSuccess?: () => void;
}
const toRecapSource = (source: InKindRedemptionEntrySource): VaultV2RecapSource => source === 'position_manager' ? 'positionManager' : 'actionPanel';
const VaultV2InKindRedemptionReviewDialog = t0 => {
  const $ = _c(114);
  const {
    source,
    vaultAddress,
    chainId,
    preview,
    penalty,
    userPosition,
    market,
    dialogOptions,
    onTxSuccess
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    account,
    targetChain,
    switchToTargetChain
  } = useWeb3Context();
  const {
    data: client
  } = useWalletClient();
  const queryClient = useQueryClient();
  const [reviewSessionId] = useState(_temp);
  useCloseDialogOnWalletChange();
  const containerRef = useFocusOnMount();
  let t1;
  if ($[0] !== preview.marketParams) {
    t1 = [preview.marketParams];
    $[0] = preview.marketParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== chainId || $[3] !== preview.exitAssets || $[4] !== t1 || $[5] !== vaultAddress) {
    t2 = {
      vaultAddress,
      chainId,
      exitAssets: preview.exitAssets,
      marketParamsList: t1
    };
    $[2] = chainId;
    $[3] = preview.exitAssets;
    $[4] = t1;
    $[5] = vaultAddress;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    vaultData,
    userVaultShares,
    safeExitAssets,
    transaction,
    requirements,
    areRequirementsReady,
    sdkBuildError,
    error,
    isLoading
  } = useVaultV2InKindRedeemTransaction(t2);
  let t3;
  if ($[7] !== chainId || $[8] !== reviewSessionId || $[9] !== sdkBuildError) {
    t3 = {
      sdkAction: "inKindRedeem",
      chainId,
      reviewSessionId,
      error: sdkBuildError
    };
    $[7] = chainId;
    $[8] = reviewSessionId;
    $[9] = sdkBuildError;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useLogSdkBuildFailed(t3);
  const [isPreparingSettled] = useDebounce(isLoading, PREPARATION_SPINNER_DELAY_MS);
  let t4;
  if ($[11] !== penalty || $[12] !== vaultData) {
    t4 = vaultData !== undefined && inKindRedemptionPenaltyRate(vaultData) === penalty;
    $[11] = penalty;
    $[12] = vaultData;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const isPenaltyCurrent = t4;
  let t5;
  if ($[14] !== error || $[15] !== isPenaltyCurrent || $[16] !== vaultData) {
    t5 = isDefined(error) || vaultData !== undefined && !isPenaltyCurrent;
    $[14] = error;
    $[15] = isPenaltyCurrent;
    $[16] = vaultData;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const hasPreparationError = t5;
  const isBlueBalanceError = error instanceof InsufficientBlueBalanceForInKindRedeemError;
  let t6;
  if ($[18] !== source) {
    t6 = toRecapSource(source);
    $[18] = source;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const recapSource = t6;
  let t7;
  if ($[20] !== recapSource || $[21] !== vaultAddress) {
    t7 = {
      vaultAddress,
      source: recapSource
    };
    $[20] = recapSource;
    $[21] = vaultAddress;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const recap = useVaultV2RecapData(t7);
  const isRecapMissing = !recap.loading && (!recap.vaultV2 || !recap.vaultAsset);
  let t8;
  if ($[23] !== account.isWalletTypeResolved || $[24] !== client || $[25] !== hasPreparationError || $[26] !== isRecapMissing) {
    t8 = !isDefined(client) || !account.isWalletTypeResolved || hasPreparationError || isRecapMissing;
    $[23] = account.isWalletTypeResolved;
    $[24] = client;
    $[25] = hasPreparationError;
    $[26] = isRecapMissing;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const isHardBlocked = t8;
  let t9;
  if ($[28] !== areRequirementsReady || $[29] !== isLoading || $[30] !== recap.loading || $[31] !== transaction) {
    t9 = isDefined(transaction) && areRequirementsReady && !isLoading && !recap.loading;
    $[28] = areRequirementsReady;
    $[29] = isLoading;
    $[30] = recap.loading;
    $[31] = transaction;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const isPrepared = t9;
  const canMigrate = isPrepared && !isHardBlocked;
  let t10;
  if ($[33] !== account.isSafe || $[34] !== requirements || $[35] !== reviewSessionId || $[36] !== source) {
    t10 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint: source,
      operationTypes: [InterfaceOperation.InterfaceOperationType.vaultV2InKindRedeem],
      isSafe: account.isSafe,
      requirements
    });
    $[33] = account.isSafe;
    $[34] = requirements;
    $[35] = reviewSessionId;
    $[36] = source;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const reviewTrackingParams = t10;
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, areRequirementsReady && !isLoading);
  let t11;
  if ($[38] !== preview || $[39] !== vaultData) {
    t11 = isDefined(vaultData) ? {
      ...preview,
      idleAssets: vaultData.assetBalance
    } : preview;
    $[38] = preview;
    $[39] = vaultData;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== penalty || $[42] !== safeExitAssets || $[43] !== t11) {
    t12 = toSafeInKindPreview(t11, safeExitAssets, penalty);
    $[41] = penalty;
    $[42] = safeExitAssets;
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const safePreview = t12;
  let t13;
  if ($[45] !== userPosition || $[46] !== userVaultShares || $[47] !== vaultData) {
    t13 = isDefined(vaultData) && isDefined(userVaultShares) ? vaultData.toAssets(userVaultShares) : userPosition;
    $[45] = userPosition;
    $[46] = userVaultShares;
    $[47] = vaultData;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const livePosition = t13;
  let t14;
  if ($[49] !== livePosition || $[50] !== safeExitAssets) {
    t14 = MathLib.zeroFloorSub(livePosition, safeExitAssets);
    $[49] = livePosition;
    $[50] = safeExitAssets;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  const finalPosition = t14;
  let t15;
  if ($[52] !== market.collateralAsset || $[53] !== market.loanAsset) {
    t15 = getMarketName({
      collateralAsset: market.collateralAsset,
      loanAsset: market.loanAsset,
      maxLength: 20
    });
    $[52] = market.collateralAsset;
    $[53] = market.loanAsset;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const marketName = t15;
  let t16;
  if ($[55] !== finalPosition || $[56] !== livePosition || $[57] !== market || $[58] !== penalty || $[59] !== recapSource || $[60] !== safePreview || $[61] !== vaultAddress) {
    t16 = {
      vaultAddress,
      preview: safePreview,
      initialPosition: livePosition,
      finalPosition,
      market,
      penalty,
      source: recapSource
    };
    $[55] = finalPosition;
    $[56] = livePosition;
    $[57] = market;
    $[58] = penalty;
    $[59] = recapSource;
    $[60] = safePreview;
    $[61] = vaultAddress;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  const recapProps = t16;
  let t17;
  if ($[63] !== account || $[64] !== canMigrate || $[65] !== client || $[66] !== confirmAttempt || $[67] !== dialogOptions || $[68] !== onTxSuccess || $[69] !== preview.marketParams || $[70] !== queryClient || $[71] !== recapProps || $[72] !== requirements || $[73] !== submitTransactions || $[74] !== targetChain || $[75] !== toggleDialog || $[76] !== transaction || $[77] !== vaultAddress || $[78] !== vaultData) {
    t17 = () => {
      const recipient = account.address;
      const assetAddress = vaultData?.asset;
      if (!canMigrate || !transaction || !client || !recipient || !assetAddress) {
        return;
      }
      const checkoutTracking = confirmAttempt();
      submitTransactions(client, {
        actionType: InterfaceOperation.InterfaceOperationType.vaultV2InKindRedeem,
        flow: "vaultV2RedeemInKind",
        transaction,
        ...getSdkTracking(transaction),
        requirements,
        chain: targetChain,
        account,
        checkoutTracking,
        requiresSuccessfulSimulation: true,
        onTxSuccess: receipt => {
          const realised = isDefined(receipt) && realisedInKindRedemption(receipt.logs, {
            vaultAddress,
            marketId: preview.marketParams.id,
            assetAddress,
            userAddress: recipient,
            morphoAddress: getChainAddress(targetChain.id, "blue"),
            bundlerAddress: getChainAddress(targetChain.id, "bundles.vaultExitBundlesV1")
          });
          refetchLendPositionsUntilIndexed(queryClient, recipient);
          if (!realised) {
            return;
          }
          onTxSuccess?.();
        }
      }).catch(error_0 => {
        logger.error(error_0 instanceof Error ? error_0 : new Error(String(error_0)), {
          tags: {
            component: "VaultV2InKindRedemptionReviewDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage="In-kind Redemption" operationContent={<VaultV2InKindRedemptionRecap {...recapProps} showRealisedAmounts={true} />} token={vaultData ? new InterfaceToken({
        address: vaultData.address,
        name: vaultData.name,
        symbol: vaultData.symbol,
        decimals: vaultData.decimals,
        chain: {
          id: targetChain.id
        },
        logoURI: null
      }) : undefined} successContent={<InKindRedemptionPortfolioAlert recipient={recipient} vaultAddress={vaultAddress} marketId={preview.marketParams.id} assetAddress={vaultData?.asset} />} />, {
        ...dialogOptions,
        closable: false,
        ariaLabel: "In-kind redemption confirmation"
      });
    };
    $[63] = account;
    $[64] = canMigrate;
    $[65] = client;
    $[66] = confirmAttempt;
    $[67] = dialogOptions;
    $[68] = onTxSuccess;
    $[69] = preview.marketParams;
    $[70] = queryClient;
    $[71] = recapProps;
    $[72] = requirements;
    $[73] = submitTransactions;
    $[74] = targetChain;
    $[75] = toggleDialog;
    $[76] = transaction;
    $[77] = vaultAddress;
    $[78] = vaultData;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  const handleMigrate = t17;
  let t18;
  if ($[80] !== handleMigrate || $[81] !== isHardBlocked || $[82] !== isLoading || $[83] !== isPrepared) {
    t18 = {
      isHardBlocked,
      isPrepared,
      isRefreshSignal: isLoading,
      submit: handleMigrate
    };
    $[80] = handleMigrate;
    $[81] = isHardBlocked;
    $[82] = isLoading;
    $[83] = isPrepared;
    $[84] = t18;
  } else {
    t18 = $[84];
  }
  const {
    request: requestMigrate,
    isPending: isMigratePending,
    isDisabled: isMigrateDisabled
  } = useReviewConfirmGate(t18);
  const showPreparing = isLoading && isPreparingSettled || isMigratePending;
  let t19;
  if ($[85] !== toggleDialog || $[86] !== trackClose) {
    t19 = () => {
      trackClose();
      toggleDialog(false);
    };
    $[85] = toggleDialog;
    $[86] = trackClose;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  const handleClose = t19;
  let t20;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="text-title-sm">Review</span>;
    $[88] = t20;
  } else {
    t20 = $[88];
  }
  let t21;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Close />;
    $[89] = t21;
  } else {
    t21 = $[89];
  }
  let t22;
  if ($[90] !== handleClose) {
    t22 = <div className="flex items-center justify-between gap-[16px]">{t20}<Button size="icon" onClick={handleClose} variant="secondary" aria-label="Close">{t21}</Button></div>;
    $[90] = handleClose;
    $[91] = t22;
  } else {
    t22 = $[91];
  }
  let t23;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ErrorServiceUnavailable minHeight="364px" />;
    $[92] = t23;
  } else {
    t23 = $[92];
  }
  let t24;
  if ($[93] !== recapProps) {
    t24 = <SectionErrorBoundary fallback={t23}><VaultV2InKindRedemptionRecap {...recapProps} /></SectionErrorBoundary>;
    $[93] = recapProps;
    $[94] = t24;
  } else {
    t24 = $[94];
  }
  let t25;
  if ($[95] !== marketName) {
    t25 = <InfoCallout variant="warning" className="rounded-lg" data-testid="ikr-market-liquidity-callout" role="status">You receive a lending position on {marketName}. Withdrawing it later depends on that Market's liquidity.</InfoCallout>;
    $[95] = marketName;
    $[96] = t25;
  } else {
    t25 = $[96];
  }
  let t26;
  if ($[97] !== hasPreparationError || $[98] !== isBlueBalanceError || $[99] !== marketName) {
    t26 = hasPreparationError && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="ikr-preparation-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{isBlueBalanceError ? <>{marketName} has no available liquidity to redeem into right now. Choose a different Market, or try again once borrowers repay.</> : <>This redemption can't be prepared right now. The Market's liquidity may have changed — go back and pick an amount again.</>}</span></div>;
    $[97] = hasPreparationError;
    $[98] = isBlueBalanceError;
    $[99] = marketName;
    $[100] = t26;
  } else {
    t26 = $[100];
  }
  let t27;
  if ($[101] !== account.isWrongChain || $[102] !== isMigrateDisabled || $[103] !== requestMigrate || $[104] !== showPreparing || $[105] !== switchToTargetChain) {
    t27 = account.isWrongChain ? <Button size="lg" onClick={switchToTargetChain}>Switch chain</Button> : <Button onClick={() => requestMigrate()} size="lg" disabled={isMigrateDisabled}>{showPreparing ? <LoaderGradient /> : null}{showPreparing ? "Loading" : "Redeem"}</Button>;
    $[101] = account.isWrongChain;
    $[102] = isMigrateDisabled;
    $[103] = requestMigrate;
    $[104] = showPreparing;
    $[105] = switchToTargetChain;
    $[106] = t27;
  } else {
    t27 = $[106];
  }
  let t28;
  if ($[107] !== containerRef || $[108] !== t22 || $[109] !== t24 || $[110] !== t25 || $[111] !== t26 || $[112] !== t27) {
    t28 = <div ref={containerRef} tabIndex={-1} className="gap-md flex flex-col focus:outline-none" data-testid="confirm-dialog">{t22}{t24}{t25}{t26}{t27}</div>;
    $[107] = containerRef;
    $[108] = t22;
    $[109] = t24;
    $[110] = t25;
    $[111] = t26;
    $[112] = t27;
    $[113] = t28;
  } else {
    t28 = $[113];
  }
  return t28;
};
export default VaultV2InKindRedemptionReviewDialog;
function _temp() {
  return crypto.randomUUID();
}